import {
	chainCommands,
	createParagraphNear,
	liftEmptyBlock,
	newlineInCode,
	splitBlock,
	toggleMark
} from "prosemirror-commands";
import {
	liftListItem,
	sinkListItem,
	splitListItem
} from "prosemirror-schema-list";
import { redo, undo } from "prosemirror-history";
import { withRestoreDefaultFontMarksOnNewParagraph } from "./commands/withRestoreDefaultFontMarksOnNewParagraph";
import { Command } from "prosemirror-state";
import { Schema } from "prosemirror-model";
import {
	hasMarks,
	hasNodes,
	RichTextEditorMarks
} from "./schema";
import { insertHardBreak } from "./commands/insertHardBreak";

export function buildKeymap(
	schema: Schema<never, never>
): Record<string, Command> {
	const result: Record<string, Command> = {};

	const registerCommand = (keyBinding: string, command: Command): void => {
		result[keyBinding] = command;
	};

	const registerToggleMark = (keyBinding: string, mark: RichTextEditorMarks): void => {
		if (hasMarks(schema, [mark])) {
			registerCommand(keyBinding, toggleMark(schema.marks[mark]));
		}
	};

	registerToggleMark("Mod-b", "strong");
	registerToggleMark("Mod-i", "em");
	registerToggleMark("Mod-u", "underline");
	registerToggleMark("Mod-s", "strikeThrough");

	const enterCommands: Command[] = [];

	if (hasNodes(schema, ["listItem"])) {
		enterCommands.push(
			withRestoreDefaultFontMarksOnNewParagraph(
				splitListItem(schema.nodes.listItem)
			)
		);
	}

	if (hasNodes(schema, ["code_block"])) {
		enterCommands.push(newlineInCode);
	}

	if (hasNodes(schema, ["paragraph"])) {
		enterCommands.push(
			withRestoreDefaultFontMarksOnNewParagraph(
				createParagraphNear
			)
		);
	}

	enterCommands.push(
		withRestoreDefaultFontMarksOnNewParagraph(
			liftEmptyBlock
		)
	);

	enterCommands.push(
		withRestoreDefaultFontMarksOnNewParagraph(
			splitBlock
		)
	);

	registerCommand(
		"Enter",
		chainCommands(
			...enterCommands
		)
	);

	const shiftEnterCommands: Command[] = [];

	if (hasNodes(schema, ["code_block"])) {
		shiftEnterCommands.push(newlineInCode);
	}

	if (hasNodes(schema, ["hard_break"])) {
		shiftEnterCommands.push(insertHardBreak(schema));
	}

	if (shiftEnterCommands.length > 0) {
		registerCommand(
			"Shift-Enter",
			chainCommands(
				...shiftEnterCommands
			)
		);
	}

	if (hasNodes(schema, ["listItem"])) {
		registerCommand(
			"Tab",
			withRestoreDefaultFontMarksOnNewParagraph(
				sinkListItem(schema.nodes.listItem)
			)
		);

		registerCommand(
			"Shift-Tab",
			withRestoreDefaultFontMarksOnNewParagraph(
				liftListItem(schema.nodes.listItem)
			)
		);
	}

	registerCommand("Mod-z", undo);
	registerCommand("Shift-Mod-z", redo);
	registerCommand("Ctrl-y", redo);

	return result;
}
